import { createContext, useState, useContext, useEffect, useCallback } from "react";
import io from "socket.io-client";
import PropTypes from "prop-types";
import Cookies from "js-cookie";

import { fetchUserWithJwtToken } from "src/data/auth/loginFunctions";
import { asiakas_fetch } from "src/data/asiakas/AsiakasFunctions";
import userAuthClass from "src/data/auth/userAuthClass";

export const userContext = createContext();

export const UserProvider = ({ children }) => {
  const [userAsiakas, setUserAsiakas] = useState(null);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isSocketConnected, setSocketConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [auth, setAuth] = useState(new userAuthClass(user, userAsiakas));
  const [socketLogMessages, setSocketLogMessages] = useState([]);

  useEffect(() => {
    if (user) {
      setIsLoggedIn(true);
      setIsLoading(false);
      return;
    }
    const _asynk = async () => {
      let _userData = await _fetchUserByExistingToken();
      if (!_userData || !_userData.user) {
        console.log("existing token not valid");
      } else {
        doSetToken(_userData.jwtToken);
        doSetUser(_userData.user);
        setIsLoggedIn(true);
      }
      setIsLoading(false);
    };
    _asynk();
  }, [user]);

  // SET userAsiakas
  useEffect(() => {
    if (!token) return;
    if (!user) return;
    if (userAsiakas) return;
    const _asynk = async () => {
      const _userAsiakasId = user.oletusAdminYritysId;
      if (_userAsiakasId) {
        const _fetchResult = await asiakas_fetch(user, _userAsiakasId, token, null, _userAsiakasId);
        doSetUserAsiakas(_fetchResult.globalAsiakas);
      }
      setIsLoaded(true);
    };
    _asynk();
  }, [userAsiakas, user, token]);

  // Set Cookies
  useEffect(() => {
    Cookies.set("token", token, { expires: 7 });
  }, [token]);

  // Socket useEffect
  useEffect(() => {
    // client sends io.connect
    if (!user) return;
    const _socket = io(import.meta.env.VITE_SERVER_URL, {
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      randomizationFactor: 0.5,
    });

    _socket.on("disconnect", () => {
      setSocketConnected(false);
    });
    const roomId = "pid" + user.personId;
    _socket.on("connect", () => {
      setSocketConnected(true);
    });

    _socket.emit("join", roomId);
    _socket.on("reconnect", () => {
      console.log("reconnect to room: " + roomId);
      _socket.emit("join", roomId);
    });

    setSocket(_socket);

    return () => {
      _socket && _socket.close();
      setSocket(null);
      setSocketConnected(false);
    };
  }, [user]);

  useEffect(() => {
    if (!socket) return;
    socket.on("socketLog:update", (obj) => {
      console.log("socketLog:update message received", obj);
      setSocketLogMessages((prev) => [...prev, obj]);
    });
    return () => {
      socket && socket.off("socketLog:update");
      setSocketLogMessages([]);
    };
  }, [socket]);

  // Set auth
  useEffect(() => {
    if (!user) return;
    if (userAsiakas && !userAsiakas.auth?.isLoaded) return;
    const _userAuthClass = new userAuthClass(user, userAsiakas);
    _userAuthClass.init(userAsiakas);
    setAuth(_userAuthClass);
  }, [userAsiakas, user, userAsiakas?.asiakasId]);

  const doSetUser = useCallback(
    (user) => {
      if (user == null) {
        setUser(null);
        setIsLoggedIn(false);
        doSetToken(null);
      } else {
        if (user.accessToken) doSetToken(user.accessToken);
        setUser(user);
        setIsLoggedIn(true);
      }
    },
    [user]
  );
  const doSetToken = useCallback(
    (token) => {
      setToken(token);
    },
    [token]
  );

  const doSetUserAsiakas = useCallback(
    (asiakas) => {
      setUserAsiakas(asiakas);
    },
    [userAsiakas]
  );

  const _fetchUserByExistingToken = async () => {
    const cookieJwtToken = Cookies.get("token");
    if (!cookieJwtToken) return null;
    if (cookieJwtToken.length < 5) return null;
    const userData = await fetchUserWithJwtToken(cookieJwtToken);
    return userData;
  };

  return (
    <userContext.Provider
      value={{
        user,
        doSetUser,
        token,
        doSetToken,
        socket,
        isSocketConnected,
        isLoading,
        isLoaded,
        isLoggedIn,
        userAsiakas,
        doSetUserAsiakas,
        ownerAsiakasId: userAsiakas?.asiakasId,
        auth,
        socketLogMessages,
      }}>
      {children}
    </userContext.Provider>
  );
};

export const useUserData = () => useContext(userContext);

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
  person: PropTypes.object,
  cookieJwt: PropTypes.string,
  loading: PropTypes.bool,
};
